import React, { useState, useEffect } from 'react';
import Button from '../../components/common/button/Button';
import Modal from '../../components/common/model/Modal';
import styles from './PaymentList.module.scss';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import paymentService from '../../services/paymentService';
import InputField from '../../components/common/inputField/InputField';
import { CustomerPayment, CreatePaymentAllocationData, OutstandingInvoice } from '../../types/paymentTypes';
import customerService from '../../services/customerService';
import { Customer } from '../../types/customerTypes';

const PaymentList: React.FC<{ role?: string }> = ({ role = 'admin' }) => {
  const [payments, setPayments] = useState<CustomerPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<CustomerPayment | null>(null);
  const [outstandingInvoices, setOutstandingInvoices] = useState<OutstandingInvoice[]>([]);
  const [form, setForm] = useState({
    customer_id: '',
    payment_date: new Date().toISOString().split('T')[0],
    amount: '',
    payment_method: 'cash' as 'cash' | 'card' | 'bank_transfer' | 'cheque' | 'upi',
    reference_number: '',
    notes: ''
  });
  const [allocations, setAllocations] = useState<CreatePaymentAllocationData[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMethod, setFilterMethod] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editingPaymentId, setEditingPaymentId] = useState<string | null>(null);
  const [date, setDate] = useState<string>(new Date().toLocaleDateString('en-CA'));
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      // const filters = filterMethod ? { payment_method: filterMethod } : undefined;
      const filters = {
        status: filterMethod || undefined,
        date: date || undefined,
      };
      const response = await paymentService.getPayments(filters);
      // Ensure response is always an array
      setPayments(Array.isArray(response) ? response : []);
    } catch (err) {
      console.error('Error fetching payments:', err);
      toast.error('Failed to fetch payments');
      // Set to empty array on error to prevent undefined
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await customerService.getCustomers();
      setCustomers(Array.isArray(response) ? response : []);
    } catch (err) {
      console.error('Error fetching customers:', err);
      toast.error('Failed to fetch customers');
    }
  };

  useEffect(() => {
    fetchPayments();
    fetchCustomers();
  }, [filterMethod]);

  useEffect(() => {
    fetchPayments();
    fetchCustomers();
  }, [date]);

  const fetchOutstandingInvoices = async (customerId: number) => {
    try {
      const invoices = await paymentService.getOutstandingInvoices(customerId);
      setOutstandingInvoices(Array.isArray(invoices) ? invoices : []);
    } catch (err) {
      console.error('Error fetching outstanding invoices:', err);
      toast.error('Failed to fetch outstanding invoices');
      setOutstandingInvoices([]);
    }
  };

  const handleCustomerChange = (customerId: string) => {
    setForm({ ...form, customer_id: customerId });
    if (customerId) {
      fetchOutstandingInvoices(parseInt(customerId));
      setAllocations([]);
    } else {
      setOutstandingInvoices([]);
      setAllocations([]);
    }
  };

  const handleAllocationChange = (invoiceId: number, amount: number) => {
    setAllocations(prev => {
      const existing = prev.find(a => a.invoice_id === invoiceId);
      if (amount === 0) {
        return prev.filter(a => a.invoice_id !== invoiceId);
      }
      if (existing) {
        return prev.map(a =>
          a.invoice_id === invoiceId ? { ...a, allocated_amount: amount } : a
        );
      }
      return [...prev, { invoice_id: invoiceId, allocated_amount: amount }];
    });
  };

  const getTotalAllocated = () => {
    return allocations.reduce((sum, alloc) => sum + alloc.allocated_amount, 0);
  };

  const handleAdd = async () => {
    if (!form.customer_id || !form.amount) {
      toast.error('Please fill in customer and amount');
      return;
    }

    const paymentAmount = parseFloat(form.amount);
    const totalAllocated = getTotalAllocated();

    if (totalAllocated > paymentAmount) {
      toast.error('Total allocated amount cannot exceed payment amount');
      return;
    }

    const data = {
      customer_id: parseInt(form.customer_id),
      payment_date: form.payment_date,
      amount: paymentAmount,
      payment_method: form.payment_method,
      reference_number: form.reference_number,
      notes: form.notes,
      allocations: allocations.length > 0 ? allocations : undefined
    };

    try {
      await paymentService.createPayment(data);
      toast.success('Payment created successfully!');
      setShowModal(false);
      resetForm();
      fetchPayments();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create payment';
      toast.error(message);
    }
  };

  const handleCustomerSelect = (customer: Customer) => {
    setSelectedCustomer(customer);
    setForm({ ...form, customer_id: customer.id.toString() });
    setCustomerSearch(`${customer.name} - ${customer.phone}`);
    setShowCustomerDropdown(false);
  };

  const handleSaveEdit = async () => {
    if (!editingPaymentId) return;

    const paymentAmount = parseFloat(form.amount);
    const totalAllocated = getTotalAllocated();

    if (totalAllocated > paymentAmount) {
      toast.error('Total allocated amount cannot exceed payment amount');
      return;
    }

    const data = {
      id: editingPaymentId,
      customer_id: parseInt(form.customer_id),
      payment_date: form.payment_date,
      amount: paymentAmount,
      payment_method: form.payment_method,
      reference_number: form.reference_number,
      notes: form.notes,
      allocations: allocations.length > 0 ? allocations : undefined
    };

    try {
      await paymentService.updatePayment(data);
      toast.success('Payment updated successfully!');
      setShowModal(false);
      setEditMode(false);
      setEditingPaymentId(null);
      resetForm();
      fetchPayments();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update payment';
      toast.error(message);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this payment? This will reverse all allocations.')) {
      return;
    }

    try {
      await paymentService.deletePayment(id);
      toast.success('Payment deleted successfully!');
      fetchPayments();
    } catch (err) {
      toast.error('Failed to delete payment');
    }
  };

  const handleViewPayment = async (id: string) => {
    try {
      const payment = await paymentService.getPaymentById(id);
      setSelectedPayment(payment);
      setShowViewModal(true);
    } catch (err) {
      toast.error('Failed to load payment details');
    }
  };

  const handleOpenEditModal = async (payment: CustomerPayment) => {
    try {
      const fullPayment = await paymentService.getPaymentById(payment.id);
      setForm({
        customer_id: fullPayment.customer_id.toString(),
        payment_date: fullPayment.payment_date,
        amount: fullPayment.amount.toString(),
        payment_method: fullPayment.payment_method,
        reference_number: fullPayment.reference_number || '',
        notes: fullPayment.notes || ''
      });

      const customer = customers.find(c => Number(c.id) === fullPayment.customer_id);
      if (customer) {
        setSelectedCustomer(customer);
        setCustomerSearch(customer.name);
      }


      await fetchOutstandingInvoices(fullPayment.customer_id);

      if (fullPayment.allocations) {
        setAllocations(fullPayment.allocations.map(a => ({
          invoice_id: a.invoice_id,
          allocated_amount: a.allocated_amount
        })));
      }

      setEditingPaymentId(fullPayment.id);
      setEditMode(true);
      setShowModal(true);
    } catch (err) {
      toast.error('Failed to load payment for editing');
    }
  };

  const resetForm = () => {
    setForm({
      customer_id: '',
      payment_date: new Date().toISOString().split('T')[0],
      amount: '',
      payment_method: 'cash',
      reference_number: '',
      notes: ''
    });
    setAllocations([]);
    setOutstandingInvoices([]);
    setSelectedCustomer(null);
        setCustomerSearch('');
  };

  const filteredCustomers = customers.filter(customer =>
        customer.name.toLowerCase().includes(customerSearch.toLowerCase()) ||
        customer.phone?.includes(customerSearch)
    );

  // Safely filter payments - ensure payments is always an array
  const filteredPayments = (payments || []).filter(payment =>
    (payment.customer_name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (payment.reference_number?.toLowerCase() || '').includes(searchTerm.toLowerCase())
  );

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  const getPaymentMethodBadge = (method: string) => {
    const badges: any = {
      cash: { color: '#10b981', bg: '#d1fae5' },
      card: { color: '#3b82f6', bg: '#dbeafe' },
      bank_transfer: { color: '#8b5cf6', bg: '#ede9fe' },
      cheque: { color: '#f59e0b', bg: '#fef3c7' },
      upi: { color: '#ec4899', bg: '#fce7f3' }
    };
    const badge = badges[method] || badges.cash;
    return (
      <span style={{
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '0.75rem',
        fontWeight: '600',
        backgroundColor: badge.bg,
        color: badge.color
      }}>
        {method.toUpperCase().replace('_', ' ')}
      </span>
    );
  };

  return (
    <div className={styles.container}>
      <h4>Customer Payments</h4>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.searchContainer}>
            <div className={styles.searchWrapper}>
              <svg className={styles.searchIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                placeholder="Search by customer or reference..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.searchInput}
              />
              {searchTerm && (
                <button className={styles.clearButton} onClick={() => setSearchTerm('')}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              )}
            </div>
          </div>
          <div className={styles.filterWrapper}>
            <select
              value={filterMethod}
              onChange={(e) => setFilterMethod(e.target.value)}
              className={styles.filterSelect}
            >
              <option value="">All Methods</option>
              <option value="cash">Cash</option>
              <option value="card">Card</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="cheque">Cheque</option>
              <option value="upi">UPI</option>
            </select>

            <span className={styles.selectIcon}>⌄</span>
          </div>

          <input
            type="date"
            value={date}
            max={new Date().toLocaleDateString('en-CA')}
            onChange={(e) => {
              setDate(e.target.value);
            }}
            className={styles.dateFilter}
            placeholder="Select date..."
          />
        </div>
        <div className={styles.headerRight}>
          {/* {role === 'admin' && ( */}
            <Button variant="primary" onClick={() => setShowModal(true)}>
              + Add Payment
            </Button>
          {/* )} */}
        </div>
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Customer</th>
              <th>Phone</th>
              <th>Payment Date</th>
              <th>Amount</th>
              <th>Method</th>
              <th>Reference</th>
              <th>Created By</th>
              {role === 'admin' && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={role === 'admin' ? 8 : 7} style={{ textAlign: 'center', padding: '20px' }}>
                  Loading payments...
                </td>
              </tr>
            ) : filteredPayments.length > 0 ? (
              filteredPayments.map(payment => (
                <tr key={payment.id}>
                  <td>{payment.customer_name || '-'}</td>
                  <td>{payment.customer_phone || '-'}</td>
                  <td>{new Date(payment.created_at).toLocaleDateString()}</td>
                  <td>{formatCurrency(payment.amount)}</td>
                  <td>{getPaymentMethodBadge(payment.payment_method)}</td>
                  <td>{payment.reference_number || '-'}</td>
                  <td>{payment.employee_name || '-'}</td>
                  {role === 'admin' && (
                    <td>
                      <div className={styles.actionIcons}>
                        <Button onClick={() => handleViewPayment(payment.id)} className={styles.actionButton}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                            <circle cx="12" cy="12" r="3"></circle>
                          </svg>
                        </Button>
                        <Button onClick={() => handleOpenEditModal(payment)} className={styles.actionButton}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 20h9" />
                            <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19.5 3 21l1.5-4L16.5 3.5z" />
                          </svg>
                        </Button>
                        <Button onClick={() => handleDelete(payment.id)} className={styles.actionButton}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6"></polyline>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"></path>
                          </svg>
                        </Button>
                      </div>
                    </td>
                  )}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={role === 'admin' ? 8 : 7} style={{ textAlign: 'center', padding: '20px' }}>
                  No payments found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Payment Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditMode(false);
          setEditingPaymentId(null);
          resetForm();
        }}
        title={editMode ? "Edit Payment" : "Add Payment"}
        size="lg"
      >
        <div className={styles.modalForm}>
          <div className={styles.formRow}>
           <div className={styles.searchableField}>
                            <label>Customer *</label>
                            <div className={styles.dropdownContainer}>
                                <input
                                    type="text"
                                    value={customerSearch}
                                    onChange={(e) => {
                                        setCustomerSearch(e.target.value);
                                        setShowCustomerDropdown(true);
                                    }}
                                    onFocus={() => setShowCustomerDropdown(true)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault(); // prevents form submit if inside <form>
                                            setShowCustomerDropdown(true);
                                        }
                                    }}
                                    placeholder="Search customer by name or phone..."
                                    className={styles.searchInput1}
                                    disabled={editMode}
                                />

                                {showCustomerDropdown && !editMode && filteredCustomers.length > 0 && (
                                    <div className={styles.dropdown}>
                                        {filteredCustomers.map(customer => (
                                            <div
                                                key={customer.id}
                                                className={styles.dropdownItem}
                                                onClick={() => handleCustomerSelect(customer)}
                                            >
                                                <div className={styles.customerInfo}>
                                                    <strong>{customer.name} | </strong>
                                                    <small>{customer.phone}</small>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            {/* {selectedCustomer && (
                <div className={styles.selectedCustomer}>
                  <strong>{selectedCustomer.name}</strong>
                  <p>{selectedCustomer.email} | {selectedCustomer.phone}</p>
                </div>
              )} */}
                        </div>
            <div>
              <label>Payment Method</label>
              <select
                value={form.payment_method}
                onChange={e => setForm({ ...form, payment_method: e.target.value as any })}
                className={styles.filterSelect1}
              >
                <option value="cash">Cash</option>
                <option value="card">Card</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="cheque">Cheque</option>
                <option value="upi">UPI</option>
              </select>
            </div>
            {/* <InputField
              label="Payment Date"
              type="date"
              name='payment_date'
              value={form.payment_date}
              onChange={e => setForm({ ...form, payment_date: e.target.value })}
              required
            /> */}
          </div>

          <div className={styles.formRow}>
            <InputField
              label="Amount"
              type="number"
              name='amount'
              value={form.amount}
              onChange={e => setForm({ ...form, amount: e.target.value })}
              placeholder="0.00"
              required
            />

            <InputField
              label="Reference Number"
              type="text"
              name='reference_number'
              value={form.reference_number}
              onChange={e => setForm({ ...form, reference_number: e.target.value })}
              placeholder="Transaction reference"
            />

          </div>

          {/* {outstandingInvoices.length > 0 && (
            <div className={styles.allocationsSection}>
              <h5>Allocate to Invoices (Optional)</h5>
              <div className={styles.allocationList}>
                {outstandingInvoices.map(invoice => {
                  const allocation = allocations.find(a => a.invoice_id === parseInt(invoice.id));
                  return (
                    <div key={invoice.id} className={styles.allocationItem}>
                      <div className={styles.invoiceInfo}>
                        <strong>{invoice.invoice_number}</strong>
                        <span>Balance: {formatCurrency(invoice.balance_amount)}</span>
                      </div>
                      <InputField
                        type="number"
                        name='allocated_amount'
                        value={allocation?.allocated_amount || ''}
                        onChange={e => handleAllocationChange(
                          parseInt(invoice.id),
                          parseFloat(e.target.value) || 0
                        )}
                        placeholder="0.00"
                      />
                    </div>
                  );
                })}
              </div>
              <div className={styles.allocationSummary}>
                <p>Total Allocated: {formatCurrency(getTotalAllocated())}</p>
                <p>Unallocated: {formatCurrency(parseFloat(form.amount || '0') - getTotalAllocated())}</p>
              </div>
            </div>
          )} */}

          <InputField
            label="Notes"
            type="textarea"
            name='notes'
            value={form.notes}
            onChange={e => setForm({ ...form, notes: e.target.value })}
            placeholder="Additional notes..."
          />

          <div className={styles.modalActions}>
            <Button variant="secondary" onClick={() => {
              setShowModal(false);
              setEditMode(false);
              setEditingPaymentId(null);
              resetForm();
            }}>
              Cancel
            </Button>
            <Button variant="primary" onClick={editMode ? handleSaveEdit : handleAdd}>
              {editMode ? 'Update Payment' : 'Add Payment'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* View Payment Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setSelectedPayment(null);
        }}
        title="Payment Details"
        size="md"
      >
        {selectedPayment && (
          <div className={styles.viewPayment}>
            <div className={styles.paymentInfo}>
              <p><strong>Customer:</strong> {selectedPayment.customer_name}</p>
              <p><strong>Phone:</strong> {selectedPayment.customer_phone}</p>
              <p><strong>Date:</strong> {new Date(selectedPayment.created_at).toLocaleDateString()}</p>
              <p><strong>Amount:</strong> {formatCurrency(selectedPayment.amount)}</p>
              <p><strong>Method:</strong> {getPaymentMethodBadge(selectedPayment.payment_method)}</p>
              {selectedPayment.reference_number && (
                <p><strong>Reference:</strong> {selectedPayment.reference_number}</p>
              )}
              <p><strong>Created By:</strong> {selectedPayment.employee_name}</p>
            </div>

            {/* {selectedPayment.allocations && selectedPayment.allocations.length > 0 && (
              <div className={styles.allocationsView}>
                <h4>Invoice Allocations</h4>
                <table>
                  <thead>
                    <tr>
                      <th>Invoice</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedPayment.allocations.map(alloc => (
                      <tr key={alloc.id}>
                        <td>{alloc.invoice_number}</td>
                        <td>{formatCurrency(alloc.allocated_amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )} */}

            {selectedPayment.notes && (
              <div className={styles.paymentNotes}>
                <h4>Notes</h4>
                <p>{selectedPayment.notes}</p>
              </div>
            )}
          </div>
        )}
      </Modal>

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default PaymentList;